/**
 * VoiceLogFeed — public list of approved visitor voice logs.
 * Streams audio from /api/voice_logs/{id}/audio via a plain <audio> element,
 * which handles buffering + inline playback across iOS Safari, Android Chrome
 * and every desktop browser without extra work.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Radio } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/Reveal";

function relTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function VoiceLogFeed({ region = "", location = "", limit = 6 }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const params = {};
        if (region) params.region = region;
        if (location) params.location = location;
        setLoading(true);
        api.get("/voice_logs", { params })
            .then((r) => setItems((r.data || []).slice(0, limit)))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [region, location, limit]);

    if (loading) {
        return (
            <div className="text-sm text-[var(--sb-text-muted)] uppercase tracking-[0.28em] py-6" data-testid="voice-log-feed-loading">
                Loading voice logs…
            </div>
        );
    }
    if (items.length === 0) {
        return (
            <div className="text-sm text-[var(--sb-text-muted)] py-6" data-testid="voice-log-feed-empty">
                No voice logs yet. Be the first to leave one.
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="voice-log-feed">
            {items.map((v, i) => (
                <Reveal key={v.id} delay={0.05 + i * 0.05}>
                    <motion.article
                        className="border border-[var(--sb-border)] hover:border-[var(--sb-brand)]/60 transition-colors p-5"
                        data-testid={`voice-log-item-${v.id}`}
                        whileHover={{ y: -2 }}
                    >
                        <header className="flex items-start justify-between gap-3 mb-3">
                            <div>
                                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em]" style={{ color: "#0055A4" }}>
                                    <Radio size={12} /> Voice · {relTime(v.created_at)}
                                </div>
                                <h4 className="font-serif text-lg tracking-tight mt-1 leading-tight">
                                    {v.name}
                                    <span className="text-[var(--sb-text-muted)] font-sans text-sm normal-case tracking-normal"> from </span>
                                    <span className="italic" style={{ color: "#0055A4" }}>{v.location}</span>
                                </h4>
                                {v.region && (
                                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[var(--sb-text-muted)] mt-1">
                                        <MapPin size={10} /> {v.region}
                                    </div>
                                )}
                            </div>
                        </header>
                        {v.message && (
                            <p className="text-sm text-[var(--sb-text-dim)] italic mb-3 leading-relaxed">&ldquo;{v.message}&rdquo;</p>
                        )}
                        <audio
                            src={v.audio_url}
                            controls
                            preload="metadata"
                            className="w-full"
                            data-testid={`voice-log-audio-${v.id}`}
                        />
                    </motion.article>
                </Reveal>
            ))}
        </div>
    );
}
